import {
  extendEnumWithNotDisclosed,
  NotDisclosed,
  RfpAgeReductionScheduleType,
} from "@platform/openapi-types";

import { BenefitDefinition } from "../../../benefits/benefit.types";
import { CellSpec } from "../../shared/cellSpecs";
import { ageReductionScheduleDetails, enumDropdown } from "../cellSpecs/common";

import {
  applyCellEnumValues,
  createTypeDefinition,
  createValueDefinition,
} from "./builderUtils";

export interface AgeReductionScheduleBenefitPairConfig {
  prefix?: string;
  typeLabel?: string;
  typeCell?: CellSpec;
  typeEnumValues?: string[];
  schedulesLabel?: string;
  schedulesCell?: CellSpec;
  typeDescription?: string;
  schedulesDescription?: string;
  disabledValues?: string[];
  nested?: boolean;
  networkDependent?: boolean;
  typeDefaultValue?: unknown;
  schedulesDefaultValue?: unknown;
  isRequired?: boolean;
  isValueRequired?: boolean;
}

/**
 * Builds an age reduction schedule benefit definition pair.
 *
 * The schedules field's editability depends on the type field's value.
 * Used by Basic Life, Critical Illness, and Voluntary Life.
 */
export function buildAgeReductionScheduleBenefitPair<
  TPrefix extends string = "ageReductionSchedule",
>(
  config: AgeReductionScheduleBenefitPairConfig & {
    prefix?: TPrefix;
  } = {} as AgeReductionScheduleBenefitPairConfig & { prefix?: TPrefix },
): {
  [K in `${TPrefix}Type` | `${TPrefix}Schedules`]: BenefitDefinition;
} {
  const {
    prefix = "ageReductionSchedule",
    typeLabel = "Age Reduction Schedule",
    typeCell = enumDropdown,
    typeEnumValues = extendEnumWithNotDisclosed(RfpAgeReductionScheduleType),
    schedulesLabel = "Schedule Details",
    schedulesCell = ageReductionScheduleDetails,
    typeDescription = "Details any benefit change-increments based on age",
    schedulesDescription = "Configure age bands and reduction percentages",
    disabledValues = ["notIncluded", NotDisclosed],
    nested = true,
    networkDependent = false,
    typeDefaultValue,
    schedulesDefaultValue = [{ reductionPercent: 50, age: 65 }],
    isRequired,
    isValueRequired,
  } = config;

  const finalTypeCell = applyCellEnumValues(
    typeCell,
    typeEnumValues,
    typeDefaultValue,
  );

  const typeDef = createTypeDefinition(
    typeLabel,
    typeDescription,
    finalTypeCell,
    networkDependent,
    prefix,
    "Schedules",
    typeDefaultValue,
    isRequired,
  );

  const schedulesDef = createValueDefinition(
    schedulesLabel,
    schedulesDescription,
    schedulesCell,
    networkDependent,
    nested,
    prefix,
    disabledValues,
    schedulesDefaultValue,
    undefined,
    isValueRequired,
  );

  return {
    [`${prefix}Type`]: typeDef,
    [`${prefix}Schedules`]: schedulesDef,
  } as {
    [K in `${TPrefix}Type` | `${TPrefix}Schedules`]: BenefitDefinition;
  };
}
